import { useState, RefObject, useCallback, useRef } from 'react';
import useResizeObserver from '@creditflowresearch/uilib/hooks/useResizeObserver';
import { ISelectOption } from '../../interfaces';

interface IProps {
  value: ISelectOption[],
  containerRef: RefObject<HTMLDivElement>,
}

const TAG_GAP_SIZE = 4;

export const useMultiValueContainer = ({ containerRef, value }: IProps) => {
  const selectedValuesCount = Array.isArray(value) ? value.length : 0;
  const containerElement = containerRef.current?.firstChild as HTMLElement;

  const [displayCount, setDisplayCount] = useState<number>(selectedValuesCount);
  const [tagWidths, setTagWidths] = useState(new Map<number, number>());
  const counterRef = useRef<HTMLDivElement>(null);

  const registerTagSize = useCallback((key: number, width: number | null) => {
    setTagWidths((origin) => {
      const clone = new Map(origin);
      clone.set(key, width);
      return clone;
    });

    return function unregister(key: number) {
      setTagWidths((origin) => {
        const clone = new Map(origin);
        clone.delete(key);
        return clone;
      });
    };
  }, []);

  const onResize = useCallback(({ width: containerWidth }: { width: number }) => {
    if (!containerWidth || selectedValuesCount === 0) {
      setDisplayCount(selectedValuesCount);
      return;
    }

    let totalWidth = 0;
    let count = 0;

    for (let i = 0; i < selectedValuesCount; i++) {
      const itemWidth = tagWidths.get(i);

      if (itemWidth === undefined) {
        setDisplayCount(Math.max(0, i - 1));
        return;
      }

      const currentWidth = itemWidth + TAG_GAP_SIZE;

      if (i === selectedValuesCount - 1) {
        if (totalWidth + currentWidth <= containerWidth) {
          count = selectedValuesCount;
          break;
        }
      }

      const counterWidth = counterRef.current?.getBoundingClientRect().width ?? 0;

      if (totalWidth + currentWidth + counterWidth > containerWidth) {
        break;
      }

      totalWidth += currentWidth;
      count++;
    }

    setDisplayCount(count);
  }, [tagWidths, selectedValuesCount, setDisplayCount]);

  useResizeObserver(containerElement, onResize);

  return {
    counterRef,
    displayCount,
    registerTagSize,
  };
};
